import React, { FC, useEffect, useRef, useState } from 'react';

import { Row } from 'antd';
import ReactPlayer from 'react-player';
import { PageDetailEnum } from 'src/shared/enums';
import { IContentComponent, IContentDetail } from 'src/shared/models';
import { useWindowSize } from 'utils';

export enum VideoTypesEnum {
  NORMAL = '0',
  AUTOPLAY = '1',
  LIGHT = '2',
}
interface IVideo {
  component: IContentComponent;
}

export const Video: FC<IVideo> = ({ component }) => {
  const { componentDetails } = component;

  const renderItem = (detail: IContentDetail, key: number) => {
    switch (detail.type) {
      case PageDetailEnum.VIDEO:
        return <VideoDetail {...{ detail, key }} />;
      default:
        return;
    }
  };

  return (
    <>
      {componentDetails
        .filter((detail) => detail.status)
        .sort((a, b) => a.order - b.order)
        .map((detail: IContentDetail, key: number) => renderItem(detail, key))}
    </>
  );
};

interface IVideoDetail {
  detail: IContentDetail;
}

export const VideoDetail: FC<IVideoDetail> = ({ detail }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef();

  const { isMD } = useWindowSize();

  const imageDesktop = detail.value || null;
  const imageMobile = detail.valueMobile || null;
  const isNormal = String(detail.position) === VideoTypesEnum.NORMAL;
  const isAutoplay = String(detail.position) === VideoTypesEnum.AUTOPLAY;
  const isLight = String(detail.position) === VideoTypesEnum.LIGHT;
  const youtubeVideoId =
    (isValidUrl(detail.slug) && extractVideoID(detail.slug)) ||
    detail.slug ||
    null;
  const youtubeVideoPlaceholder =
    (youtubeVideoId &&
      `https://img.youtube.com/vi/${youtubeVideoId}/maxresdefault.jpg`) ||
    null;
  const url = youtubeVideoId
    ? `https://www.youtube.com/watch?v=${youtubeVideoId}`
    : detail.slug;
  const placeholderImage =
    isMD && imageMobile
      ? imageMobile
      : imageDesktop
      ? imageDesktop
      : youtubeVideoPlaceholder;

  useEffect(() => {
    /**
     * Autoplay may only work only when either the media is muted, or after
     * the user clicks or taps on the site, or (on desktop) if the user has
     * previously shown an interest in media on the site.
     * https://blog.chromium.org/2018/03/chrome-66-beta-css-typed-object-model.html
     */
    isAutoplay && setPlaying(true);
  }, [isAutoplay]);

  const handleisLightPlay = () => {
    setPlaying(true);
  };

  return (
    <Row justify="center" className="video--detail-container">
      <div className="video--detail-wrapper">
        <ReactPlayer
          className="react-player"
          ref={videoRef}
          controls
          light={isLight && placeholderImage}
          onClickPreview={handleisLightPlay}
          playIcon={<React.Fragment />}
          playing={playing}
          playsinline={true}
          muted={isAutoplay}
          url={url}
          width="100%"
          height="100%"
        />
      </div>
    </Row>
  );
};

export const extractVideoID = (url: string): string | boolean => {
  if (!url) return false;
  const regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#\&\?]*).*/;
  const match = url?.match(regExp);
  return match && match[7].length == 11 ? match[7] : false;
};

export const isValidUrl = (url: string): boolean => {
  if (!url) return false;
  try {
    new URL(url);
  } catch (e) {
    return false;
  }
  return true;
};
